import ButtonComponent, { IconSize } from '../button/button.component';
import { ReactElement, ReactNode, memo } from 'react';
import { ButtonType } from '../types/ButtonType';
import ImageComponent from '@spa/components/image-component/image.component';
import LinkComponent from '@spa/components/link/link.component';
import { Link as LinkProp } from '@spa/lib/react/hooks/useLinkAttributes';
import classNames from 'clsx';
import styles from './link-button.component.module.scss';

export interface Icon {
  readonly width: number;
  readonly height: number;
  readonly src: string;
  readonly mimeType?: string;
  readonly alt: string;
  readonly unoptimized?: boolean;
}

export interface Props {
  /** Button style Primary/Secondary/As Text */
  readonly buttonType: ButtonType;
  /** Button link */
  readonly link: LinkProp;
  /** Link forwarding disabled (optional) */
  readonly preventForwarding?: boolean;
  /** Button text bold (optional) */
  readonly bold?: boolean;
  /** Button Left Icon (optional) */
  readonly iconLeft?: Icon | null;
  /** Button Right Icon (optional) */
  readonly iconRight?: Icon | null;
  /** Icon size (optional) */
  readonly iconSize?: IconSize;
  /** Button content (optional) */
  readonly text?: ReactNode | null;
  /** Button title */
  readonly title?: string | null;
  /** Content in capital letters (optional) */
  readonly uppercase?: boolean | null;
  /** Disable button (optional) */
  readonly disabled?: boolean | null;
  /** Click handler (optional) */
  readonly onClick?: () => void;
  /** Test hook id (optional) */
  readonly dataTestId?: string;
}

function LinkButtonComponent({
  disabled = false,
  buttonType,
  bold,
  iconLeft,
  iconRight,
  text,
  uppercase = false,
  title,
  link,
  preventForwarding,
  onClick,
  iconSize = IconSize.MEDIUM,
  dataTestId
}: Props): ReactElement {
  let element: ReactElement;

  if (disabled) {
    element = (
      <ButtonComponent
        disabled={disabled}
        buttonType={buttonType}
        iconLeft={iconLeft ?? undefined}
        iconRight={iconRight ?? undefined}
        text={text ?? undefined}
        uppercase={uppercase ?? undefined}
        iconSize={iconSize}
        dataTestId={dataTestId}
      />
    );
  } else {
    const linkButtonClass = classNames({
      [styles['link-button']]: true,
      [styles['link-button--primary']]: buttonType === ButtonType.PRIMARY,
      [styles['link-button--secondary']]: buttonType === ButtonType.SECONDARY,
      [styles['link-button--tertiary']]: buttonType === ButtonType.TERTIARY,
      [styles['link-button--outline']]: buttonType === ButtonType.OUTLINE,
      [styles['link-button--as-text']]: buttonType === ButtonType.AS_TEXT,
      [styles['link-button--uppercase']]: uppercase,
      ['bold']: bold
    });

    const iconImageClass = classNames({
      [styles['link-button-icon']]: true,
      [styles['link-button-icon--extra-small']]: iconSize === IconSize.EXTRA_SMALL,
      [styles['link-button-icon--small']]: iconSize === IconSize.SMALL,
      [styles['link-button-icon--medium']]: iconSize === IconSize.MEDIUM,
      [styles['link-button-icon--large']]: iconSize === IconSize.LARGE
    });

    const leftIconImage: ReactNode = iconLeft ? (
      <ImageComponent
        src={iconLeft.src}
        alt={iconLeft.alt}
        width={iconLeft.width}
        height={iconLeft.height}
        className={iconImageClass}
        unoptimized={iconLeft.unoptimized}
      />
    ) : null;

    const rightIconImage: ReactNode = iconRight ? (
      <ImageComponent
        src={iconRight.src}
        alt={iconRight.alt}
        width={iconRight.width}
        height={iconRight.height}
        className={iconImageClass}
        unoptimized={iconRight.unoptimized}
      />
    ) : null;

    element = (
      <div className={linkButtonClass}>
        <LinkComponent
          link={link}
          className={preventForwarding ? styles['link-button__link-disabled'] : ''}
          fallbackTitle={title ?? undefined}
          onClick={onClick}
          dataTestId={dataTestId}
        >
          <span
            className={classNames({
              [styles['link-button__content']]: true,
              [styles['link-button__content--has-icons']]: iconLeft != null && iconRight != null
            })}
          >
            {leftIconImage}
            {text}
            {rightIconImage}
          </span>
        </LinkComponent>
      </div>
    );
  }

  return element;
}

export default memo(LinkButtonComponent);
